// src/components/member/AdminSoal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SharedMember.css";
import "./AdminPanel.css";
import SoalReports from "./SoalReports";
import AdminVoting from "./electionperiod/AdminVoting";
import "./electionperiod/ElectionPeriod.css";
import CropModal from "./CropModal";
import AdminCalendar from "./calendar/AdminCalendar";
import { clearAuthDisplay, setAuthDisplay } from "../../authDisplay";

const emptyProfile = {
    lastName: "",
    firstName: "",
    alias: "",
    birthSurvive: "",
    batchName: "",
    rootchapter: "",
    gtglt: "",
    mwwmi: "",
    welcome: "",
    domName: "",
};

const labelMap = {
    lastName: "Last Name: ",
    firstName: "First Name: ",
    alias: "Alias: ",
    birthSurvive: "Birth/Survive: ",
    batchName: "Batch Name: ",
    rootchapter: "Root Chapter: ",
    gtglt: "GT/GLT: ",
    mwwmi: "MWW/MI: ",
    welcome: "Welcome To: ",
    domName: "Verified By: ",
    email: "Email: ",
    mobile: "Phone Number: ",
};

const editMemberFields = [...Object.keys(emptyProfile), "email", "mobile"];
const addMemberFields = [...Object.keys(emptyProfile), "email", "mobile"];
const legacyYearPattern = /^\d{4}.{0,11}$/;
const legacyYearMessage = "Year Served must start with 4 digits and be 15 characters max. Example: 2026-1st Half.";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");
const PUBLIC = process.env.PUBLIC_URL || "";
const adminDrawerPlaceholder = `${PUBLIC}/business/DefProPic.png`;

const getAdminDrawerPhoto = (member) => {
    if (!member?.primaryid) return adminDrawerPlaceholder;
    const gender = String(member.gender || "").toLowerCase();
    if (gender === "chairman") return `${API_BASE}/api/legacy/photo/${encodeURIComponent(member.primaryid)}?t=${Date.now()}`;
    const folder =
        gender === "sigma"
            ? "Sigma-Photo"
            : "Phi-Photo";
    return `${API_BASE}/UploadPhoto/${folder}/${member.primaryid}.jpg?t=${Date.now()}`;
};

const getToken = () => {
    try {
        return localStorage.getItem("token") || "";
    } catch {
        return "";
    }
};

const getTokenIdname = () => {
    try {
        const savedIdname = localStorage.getItem("idname");
        if (savedIdname) return String(savedIdname).toLowerCase();
        const token = getToken();
        if (!token) return "";
        const payload = JSON.parse(atob(token.split(".")[1] || ""));
        return String(payload?.sub || "").toLowerCase();
    } catch {
        return "";
    }
};

export default function AdminSoal({ role = "admin", onPreview }) {
    const [activeTab, setActiveTab] = useState(() => {
        try {
            const savedTab = localStorage.getItem("admin_profile_tab");
            return ["Phi", "Sigma", "Chairman", "Archive"].includes(savedTab) ? savedTab : "Phi";
        } catch {
            return "Phi";
        }
    });
    const isComelec = role === "comelec";
    const currentIdname = useMemo(() => getTokenIdname(), []);
    const canEditOvsPassword =
        isComelec ||
        (role === "admin" && ["creator", "adminsoal"].includes(currentIdname));
    const [ovsMenuOpen, setOvsMenuOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [mobileNavTooltipVisible, setMobileNavTooltipVisible] = useState(false);
    const [mobilePreviewTooltipVisible, setMobilePreviewTooltipVisible] = useState(false);
    const [mobileOvsOpen, setMobileOvsOpen] = useState(false);
    const [adminDrawerProfile, setAdminDrawerProfile] = useState(null);
    const [showAddMemberForm, setShowAddMemberForm] = useState(false);
    const [showChairmanEntryForm, setShowChairmanEntryForm] = useState(false);
    const ovsDropdownRef = useRef(null);
    const ovsCloseTimerRef = useRef(null);
    const mobileNavTooltipTimerRef = useRef(null);
    const mobilePreviewTooltipTimerRef = useRef(null);

    useEffect(() => {
        let active = true;

        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/me`);
                const data = await res.json();
                if (!active || !res.ok || !data.ok || !data.member) return;
                setAdminDrawerProfile(data.member);
            } catch {
                if (active) setAdminDrawerProfile(null);
            }
        })();

        return () => {
            active = false;
        };
    }, []);

    const adminDrawerName = adminDrawerProfile
        ? `${adminDrawerProfile.firstname || ""} ${adminDrawerProfile.lastname || ""}`.trim()
        : currentIdname || "Admin";
    const adminDrawerRole = isComelec ? "Comelec Official" : "Administrator";
    const adminDrawerPhoto = getAdminDrawerPhoto(adminDrawerProfile);

    useEffect(() => {
        document.body.classList.toggle("admin-mobile-nav-open", mobileNavOpen);
        return () => document.body.classList.remove("admin-mobile-nav-open");
    }, [mobileNavOpen]);

    const showTemporaryMobileNavTooltip = () => {
        setMobileNavTooltipVisible(true);
        if (mobileNavTooltipTimerRef.current) {
            clearTimeout(mobileNavTooltipTimerRef.current);
        }
        mobileNavTooltipTimerRef.current = setTimeout(() => {
            setMobileNavTooltipVisible(false);
        }, 1000);
    };

    const handleMobileNavToggle = () => {
        setMobileNavOpen((open) => !open);
        showTemporaryMobileNavTooltip();
    };

    const showTemporaryMobilePreviewTooltip = () => {
        setMobilePreviewTooltipVisible(true);
        if (mobilePreviewTooltipTimerRef.current) {
            clearTimeout(mobilePreviewTooltipTimerRef.current);
        }
        mobilePreviewTooltipTimerRef.current = setTimeout(() => {
            setMobilePreviewTooltipVisible(false);
        }, 2000);
    };

    const handleMobilePreviewClick = () => {
        const isMobilePointer =
            typeof window !== "undefined" &&
            window.matchMedia?.("(max-width: 768px), (hover: none)")?.matches;

        if (isMobilePointer) {
            showTemporaryMobilePreviewTooltip();
        } else {
            setMobilePreviewTooltipVisible(false);
        }

        onPreview?.();
    };

    useEffect(() => {
        return () => {
            if (mobileNavTooltipTimerRef.current) {
                clearTimeout(mobileNavTooltipTimerRef.current);
            }
            if (mobilePreviewTooltipTimerRef.current) {
                clearTimeout(mobilePreviewTooltipTimerRef.current);
            }
        };
    }, []);

    // Form states
    const [formPhi, setFormPhi] = useState({ ...emptyProfile });
    const [formSigma, setFormSigma] = useState({ ...emptyProfile });
    const [formChair, setFormChair] = useState({ ...emptyProfile });

    // Table state
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [pageLen, setPageLen] = useState(10);
    const [page, setPage] = useState(0);
    const [orderBy, setOrderBy] = useState("primaryid");
    const [orderDir, setOrderDir] = useState("asc");
    const [tableError, setTableError] = useState("")

    // ✅ View state: Remember last view, restore "OV System" if it was active
    const [view, setView] = useState("profile");

    // For cropping photos (Phi/Sigma)
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImageFile, setCropImageFile] = useState(null);
    const [cropPrimaryID, setCropPrimaryID] = useState(null);
    const [cropGender, setCropGender] = useState(null);
    const [uploading, setUploading] = useState(false); // spinner

    // Messages
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState("");

    useEffect(() => {
        if (!saveMsg) return undefined;
        const timer = setTimeout(() => setSaveMsg(""), 3000);
        return () => clearTimeout(timer);
    }, [saveMsg]);

    // Member edit modal
    const [editMemberRow, setEditMemberRow] = useState(null);
    const [editMemberData, setEditMemberData] = useState({ ...emptyProfile });

    // Chairman (Legacy) state
    const [chairPID, setChairPID] = useState("");
    const [chairYear, setChairYear] = useState("");
    const [chairFound, setChairFound] = useState(null);
    const [chairMsg, setChairMsg] = useState("");
    const [chairBusy, setChairBusy] = useState(false);
    const [chairMatches, setChairMatches] = useState([]);
    const [chairSearchOpen, setChairSearchOpen] = useState(false);

    useEffect(() => {
        if (!chairMsg) return undefined;
        const timer = setTimeout(() => setChairMsg(""), 3000);
        return () => clearTimeout(timer);
    }, [chairMsg]);

    // ✅ Message Box State
    const [messageBox, setMessageBox] = useState({
        show: false,
        text: "",
        success: false,
    });

    // ✅ Add these new hooks near your other useStates
    const [pendingAction, setPendingAction] = useState(null);
    // ✅ Track current chairman photo
    const [currentPhotoUrl, setCurrentPhotoUrl] = useState("");


    // Legacy edit modal
    const [editRow, setEditRow] = useState(null);
    const [editYear, setEditYear] = useState("");

    // Archive (Inactive Members)
    const [archiveRows, setArchiveRows] = useState([]);
    const [archiveTotal, setArchiveTotal] = useState(0);
    const [archiveError, setArchiveError] = useState("");
    const [securityRows, setSecurityRows] = useState([]);
    const [securityLoading, setSecurityLoading] = useState(false);
    const [securityMsg, setSecurityMsg] = useState("");
    const [securitySearch, setSecuritySearch] = useState("");
    const [securityPrimaryFilter, setSecurityPrimaryFilter] = useState("all");
    const [securityRowsPerPage, setSecurityRowsPerPage] = useState(25);
    const [securityPage, setSecurityPage] = useState(1);
    const [canManageSecurityRoles, setCanManageSecurityRoles] = useState(false);
    const [securityRoleBusy, setSecurityRoleBusy] = useState("");
    const [securitySort, setSecuritySort] = useState({ key: "role", direction: "asc" });
    const [ovsPasswordForm, setOvsPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [ovsPasswordMsg, setOvsPasswordMsg] = useState("");
    const [ovsPasswordSaving, setOvsPasswordSaving] = useState(false);
    const [ovsPasswordResetting, setOvsPasswordResetting] = useState(false);
    const [ovsPasswordVisible, setOvsPasswordVisible] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });

    const genderForList = useMemo(
        () => (activeTab === "Sigma" ? "Sigma" : "Phi"),
        [activeTab]
    );

    const handleLogout = async () => {
        try {
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            localStorage.removeItem("idname");
            clearAuthDisplay();

            // 🔥 Reset view to PROFILE
            localStorage.removeItem("admin_last_view");

            // 🔥 Reset OVS session
            localStorage.removeItem("soal_admin_ovs_session");
            localStorage.removeItem("admin_preview_mode");
            localStorage.removeItem("admin_profile_tab");

            await fetch(`${API_BASE}/api/logout`, { method: "POST" }).catch(() => {});
            window.location.reload();
        } catch (err) {
            console.error("Logout error:", err);
        }
    };

    useEffect(() => {
        if (role === "admin" && ["creator", "adminsoal"].includes(currentIdname)) {
            setAuthDisplay({ role, idname: currentIdname });
        }
    }, [role, currentIdname]);

    const openVotingView = (nextView) => {
        sessionStorage.setItem("openOVS", "yes");
        setView(nextView);
        localStorage.setItem("admin_last_view", nextView);
        setOvsMenuOpen(false);
        setMobileNavOpen(false);
    };

    const openOvsPasswordView = () => {
        setView("ovsPassword");
        localStorage.setItem("admin_last_view", "ovsPassword");
        setOvsMenuOpen(false);
        setMobileNavOpen(false);
    };

    const closeOvsPasswordView = () => {
        setView("profile");
        localStorage.removeItem("admin_last_view");
        setOvsPasswordMsg("");
    };

    const openOvsMenu = () => {
        window.clearTimeout(ovsCloseTimerRef.current);
        setOvsMenuOpen(true);
    };

    const closeOvsMenuSoon = () => {
        window.clearTimeout(ovsCloseTimerRef.current);
        ovsCloseTimerRef.current = window.setTimeout(() => {
            setOvsMenuOpen(false);
        }, 220);
    };

    const openMobileAdminView = (nextView) => {
        if (nextView === "voting" || nextView === "finishedVoting") {
            sessionStorage.setItem("openOVS", "yes");
        }
        setView(nextView);
        localStorage.setItem("admin_last_view", nextView);
        setMobileNavOpen(false);
        setMobileOvsOpen(false);
    };

    const openMobileProfile = () => {
        setView("profile");
        localStorage.removeItem("admin_last_view");
        setMobileNavOpen(false);
        setMobileOvsOpen(false);
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [view]);

    const handleOvsPasswordChange = (event) => {
        const { name, value } = event.target;
        setOvsPasswordForm((prev) => ({ ...prev, [name]: value }));
        setOvsPasswordMsg("");
    };

    const toggleOvsPasswordVisible = (field) => {
        setOvsPasswordVisible((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const saveOvsPassword = async (event) => {
        event.preventDefault();
        setOvsPasswordMsg("");

        if (!canEditOvsPassword) {
            setOvsPasswordMsg("You are not allowed to update the OVS password.");
            return;
        }

        if (ovsPasswordForm.newPassword !== ovsPasswordForm.confirmPassword) {
            setOvsPasswordMsg("New passwords do not match.");
            return;
        }

        if (ovsPasswordForm.newPassword.length < 8) {
            setOvsPasswordMsg("New OVS password must be at least 8 characters.");
            return;
        }

        try {
            setOvsPasswordSaving(true);
            const res = await fetch(`${API_BASE}/api/admin/ovs-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify(ovsPasswordForm),
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setOvsPasswordMsg(data.error || "Failed to update OVS password.");
                return;
            }

            localStorage.removeItem("soal_admin_ovs_session");
            setOvsPasswordForm({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
            });
            setOvsPasswordMsg(data.message || "OVS password updated.");
        } catch {
            setOvsPasswordMsg("Network error while updating OVS password.");
        } finally {
            setOvsPasswordSaving(false);
        }
    };

    const resetOvsPassword = async () => {
        if (!canEditOvsPassword) {
            setOvsPasswordMsg("You are not allowed to reset the OVS password.");
            return;
        }

        if (!window.confirm("Reset OVS password to the default password?")) return;

        try {
            setOvsPasswordResetting(true);
            setOvsPasswordMsg("");
            const res = await fetch(`${API_BASE}/api/admin/ovs-password/reset`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setOvsPasswordMsg(data.error || "Failed to reset OVS password.");
                return;
            }

            localStorage.removeItem("soal_admin_ovs_session");
            setOvsPasswordForm({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
            });
            setOvsPasswordMsg(data.message || "OVS password reset to the default password.");
        } catch {
            setOvsPasswordMsg("Network error while resetting OVS password.");
        } finally {
            setOvsPasswordResetting(false);
        }
    };

    const currentForm =
        activeTab === "Phi"
            ? formPhi
            : activeTab === "Sigma"
                ? formSigma
                : formChair;

    const setCurrentForm =
        activeTab === "Phi"
            ? setFormPhi
            : activeTab === "Sigma"
                ? setFormSigma
                : setFormChair;

    useEffect(() => {
        setSearch("");
        setPage(0);
    }, [activeTab, genderForList]);

    useEffect(() => {
        if (!ovsPasswordMsg) return undefined;
        const timer = window.setTimeout(() => {
            setOvsPasswordMsg("");
        }, 5000);
        return () => window.clearTimeout(timer);
    }, [ovsPasswordMsg]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (!ovsDropdownRef.current?.contains(event.target)) {
                window.clearTimeout(ovsCloseTimerRef.current);
                setOvsMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            window.clearTimeout(ovsCloseTimerRef.current);
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentForm((prev) => ({ ...prev, [name]: value }));
    };

    const validate = (d) => {
        if (!d.lastName?.trim()) return "Last Name is required.";
        if (!d.firstName?.trim()) return "First Name is required.";
        return null;
    };

    useEffect(() => {
        const last = localStorage.getItem("admin_last_view");

        if (isComelec) {
            if (last === "voting" || last === "finishedVoting") {
                const hasAuth = localStorage.getItem("soal_admin_ovs_session");
                setView(hasAuth === "active" ? last : "profile");
            } else if (last === "ovsPassword" && canEditOvsPassword) {
                setView("ovsPassword");
            } else {
                setView("profile");
            }
        } else if (last === "calendar") {
            setView("calendar");
        } else if (last === "voting") {
            const hasAuth = localStorage.getItem("soal_admin_ovs_session");
            setView(hasAuth === "active" ? "voting" : "profile");
        } else if (last === "finishedVoting") {
            const hasAuth = localStorage.getItem("soal_admin_ovs_session");
            setView(hasAuth === "active" ? "finishedVoting" : "profile");
        } else if (last === "ovsPassword" && canEditOvsPassword) {
            setView("ovsPassword");
        } else if (last === "security") {
            setView("security");
        } else if (last === "report") {
            setView("report");
        } else {
            setView("profile");
        }
    }, [isComelec, canEditOvsPassword]);


    /* ---------------- SAVE MEMBER ---------------- */
    const handleSave = async () => {
        setSaveMsg("");
        const tab = activeTab === "Sigma" ? "Sigma" : "Phi";
        const payload = {
            ...currentForm,
            lastName: currentForm.lastName.trim(),
            firstName: currentForm.firstName.trim(),
        };

        const err = validate(payload);
        if (err) {
            setSaveMsg(err);
            setMessageBox({
                show: true,
                text: `❌ ${err}`,
                success: false,
            });
            return;
        }

        try {
            setSaving(true);
            const res = await fetch(
                `${API_BASE}/api/members?tab=${encodeURIComponent(tab)}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`,
                    },
                    body: JSON.stringify(payload),
                }
            );
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setSaveMsg(data?.error || `Save failed (${res.status})`);
                setMessageBox({
                    show: true,
                    text: data?.error || `❌ Save failed (${res.status}).`,
                    success: false,
                });
                return;
            }
            const savedMessage =
                data.account
                    ? `Saved. Assigned Primary ID: ${data.primaryID}. Username: ${data.account.username}. Default password: ${data.account.defaultPassword}`
                    : `Saved. Assigned Primary ID: ${data.primaryID} (table: ${data.table}). Login account was not created automatically.`;
            setSaveMsg(savedMessage);
            setMessageBox({
                show: true,
                text: `✅ ${savedMessage}`,
                success: true,
            });
            setCurrentForm({ ...emptyProfile });
            setShowAddMemberForm(false);

            setPage(0);
            await loadList({ pageIndex: 0 });
        } catch {
            setSaveMsg("Network error. Please try again.");
            setMessageBox({
                show: true,
                text: "❌ Network error. Please try again.",
                success: false,
            });
        } finally {
            setSaving(false);
        }
    };

    const exportCSV = (rows, filename = "export") => {
        if (!rows || !rows.length) {
            alert("No data to export");
            return;
        }

        // Remove internal keys if needed
        const ignored = ["id"];
        const headers = Object.keys(rows[0]).filter(k => !ignored.includes(k));

        const csv = [
            headers.join(","), // header row
            ...rows.map(row =>
                headers
                    .map(key =>
                        `"${String(row[key] ?? "").replace(/"/g, '""')}"`
                    )
                    .join(",")
            ),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    useEffect(() => {
        const goProfile = sessionStorage.getItem("forceProfile");
        if (goProfile === "yes") {
            sessionStorage.removeItem("forceProfile");

            // Redirect to Profile tab
            setView("profile");

            // Clear saved view
            localStorage.removeItem("admin_last_view");
        }
    }, []);

    useEffect(() => {
        const toProfile = () => {
            setView("profile");
            localStorage.removeItem("admin_last_view");
            sessionStorage.removeItem("openOVS"); // extra safety
        };

        window.addEventListener("force-profile", toProfile);
        return () => window.removeEventListener("force-profile", toProfile);
    }, []);

    useEffect(() => {
        if (!isComelec) return;
        if (["report", "calendar", "security"].includes(view)) {
            setView("profile");
            localStorage.removeItem("admin_last_view");
        }
    }, [isComelec, view]);


    /* ---------------- LOAD LIST ---------------- */
    const loadList = async ({ pageIndex = page } = {}) => {
        setTableError("");
        try {
            const common = {
                search,
                orderBy,
                orderDir,
                limit: String(pageLen),
                offset: String(pageIndex * pageLen),
            };
            const url =
                activeTab === "Chairman"
                    ? `${API_BASE}/api/legacy?${new URLSearchParams(common).toString()}`
                    : `${API_BASE}/api/members?${new URLSearchParams({
                        gender: genderForList,
                        ...common,
                    }).toString()}`;
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setRows(data.rows || []);
                setTotal(data.total || 0);
                setPage(pageIndex);
            } else {
                setRows([]);
                setTotal(0);
                setTableError(data?.error || "Failed to load list.");
            }
        } catch {
            setRows([]);
            setTotal(0);
            setTableError("Network error while loading list.");
        }
    };

    useEffect(() => {
        if (activeTab === "Archive") {
            loadArchiveList();
        } else {
            loadList({ pageIndex: 0 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, genderForList, pageLen, search, orderBy, orderDir]);

    /* ---------------- MEMBER EDIT ---------------- */
    const openMemberEdit = (row) => {
        setEditMemberRow(row);
        setEditMemberData({
            lastName: row.lastname || "",
            firstName: row.firstname || "",
            alias: row.alias || "",
            birthSurvive: row.birthsurvive || "",
            batchName: row.batchname || "",
            rootchapter: row.rootchapter || "",
            gtglt: row.gtglt || "",
            mwwmi: row.mwwmi || "",
            welcome: row.welcome || "",
            domName: row.domname || "",
            email: row.email || "",
            mobile: row.mobile || "",
        });

        // ✅ Set current photo URL once (avoids flicker)
        setCurrentPhotoUrl(`${API_BASE}/UploadPhoto/${activeTab}-Photo/${row.primaryid}.jpg?${Date.now()}`);
    };


    const closeMemberEdit = () => {
        setEditMemberRow(null);
        setEditMemberData({ ...emptyProfile });
    };

    /* ---------------- SAVE MEMBER EDIT ---------------- */
    const saveMemberEdit = async () => {
        if (!editMemberRow) return;
        const d = editMemberData;

        if (!d.lastName?.trim() || !d.firstName?.trim()) {
            setSaveMsg("Last/First name required.");
            setMessageBox({
                show: true,
                text: "❌ Last/First name required.",
                success: false,
            });
            return;
        }

        try {
            setSaving(true);
            const url = `${API_BASE}/api/members/${genderForList}/${editMemberRow.id}`;
            const res = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify(d),
            });

            const data = await res.json();
            if (!res.ok || !data.ok) {
                setSaveMsg(data?.error || `Update failed (${res.status})`);
                setMessageBox({
                    show: true,
                    text: data?.error || `❌ Update failed (${res.status}).`,
                    success: false,
                });
                return;
            }

            closeMemberEdit();
            await loadList({ pageIndex: page });
            setSaveMsg("Member updated.");
            setMessageBox({
                show: true,
                text: "✅ Member updated.",
                success: true,
            });
        } catch {
            setSaveMsg("Network error while updating.");
            setMessageBox({
                show: true,
                text: "❌ Network error while updating.",
                success: false,
            });
        } finally {
            setSaving(false);
        }
    };

    /* ---------------- DELETE MEMBER ---------------- */
    const deleteMember = (row) => {
        if (!row?.id) return;
        const deleteGender = genderForList;
        setMessageBox({
            show: true,
            text: `❓ Delete ${row.primaryid} (${row.lastname}, ${row.firstname})?`,
            success: null,
        });
        setPendingAction(() => async () => {
            await confirmDeleteMember(row, deleteGender);
        });
    };

    const confirmDeleteMember = async (row, deleteGender) => {
        try {
            setSaving(true);
            const url = `${API_BASE}/api/members/${deleteGender}/${row.id}`;
            const res = await fetch(url, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setSaveMsg(data?.error || `Delete failed (${res.status})`);
                setMessageBox({
                    show: true,
                    text: data?.error || `❌ Delete failed (${res.status}).`,
                    success: false,
                });
                return;
            }
            await loadList({ pageIndex: 0 });
            setSaveMsg("Member deleted.");
            setMessageBox({
                show: true,
                text: "✅ Member deleted.",
                success: true,
            });
        } catch {
            setSaveMsg("Network error while deleting.");
            setMessageBox({
                show: true,
                text: "❌ Network error while deleting.",
                success: false,
            });
        } finally {
            setSaving(false);
            setPendingAction(null);
        }
    };

    const totalPages = Math.max(Math.ceil(total / pageLen), 1);

    /* ---------------- CHAIRMAN (Legacy) ---------------- */
    useEffect(() => {
        const term = chairPID.trim();
        if (activeTab !== "Chairman" || term.length < 2 || chairFound?.primaryid === term) {
            setChairMatches([]);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                const url = `${API_BASE}/api/legacy/search?q=${encodeURIComponent(term)}`;
                const res = await fetch(url, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const data = await res.json();
                if (res.ok && data.ok) {
                    setChairMatches(data.rows || []);
                    setChairSearchOpen(true);
                }
            } catch (err) {
                if (err.name !== "AbortError") setChairMatches([]);
            }
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [activeTab, chairPID, chairFound?.primaryid]);

    const selectChairmanMatch = (member) => {
        setChairPID(member.primaryid || "");
        setChairFound(member);
        setChairMatches([]);
        setChairSearchOpen(false);
        setChairMsg("Member selected. Enter Year Served and Save.");
    };

    const saveChairman = async () => {
        setChairMsg("");

        if (!chairPID.trim()) {
            setChairMsg("⚠️ Primary ID is required.");
            return;
        }

        if (!chairYear.trim()) {
            setChairMsg("⚠️ Year Served is required.");
            return;
        }

        if (!legacyYearPattern.test(chairYear.trim())) {
            setChairMsg(`⚠️ ${legacyYearMessage}`);
            return;
        }

        try {
            setChairBusy(true);
            const res = await fetch(`${API_BASE}/api/legacy`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({
                    primaryID: (chairFound?.primaryid || chairPID).trim(),
                    yearServed: chairYear.trim(),
                }),
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setChairMsg(data?.error || "Save failed.");
                return;
            }

            setChairMsg("✅ Saved successfully.");
            setChairPID("");
            setChairYear("");
            setChairFound(null);
            setChairMatches([]);
            setChairSearchOpen(false);
            setShowChairmanEntryForm(false);
            await loadList({ pageIndex: 0 });
        } catch {
            setChairMsg("❌ Network error, please try again.");
        } finally {
            setChairBusy(false);
        }
    };

    const openEdit = (row) => {
        setEditRow(row);
        setEditYear(row.yearserved || "");
        setCurrentPhotoUrl(`${API_BASE}/api/legacy/photo/${encodeURIComponent(row.primaryid)}?t=${Date.now()}`);
    };

    const closeEdit = () => {
        setEditRow(null);
        setEditYear("");
    };

    const saveEditRow = async () => {
        if (!editRow) return;
        const ys = (editYear || "").trim();
        if (!legacyYearPattern.test(ys)) {
            setChairMsg(legacyYearMessage);
            return;
        }
        try {
            setChairBusy(true);
            const res = await fetch(`${API_BASE}/api/legacy/${editRow.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ yearServed: ys }),
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setChairMsg(data?.error || `Update failed (${res.status})`);
                return;
            }
            closeEdit();
            await loadList({ pageIndex: page });
            setChairMsg("Legacy entry updated.");
        } catch {
            setChairMsg("Network error while updating.");
        } finally {
            setChairBusy(false);
        }
    };

    /* ---------------- DELETE ROW ---------------- */
    const deleteRow = (row) => {
        if (!row?.id) return;
        setMessageBox({
            show: true,
            text: `❓ Delete legacy entry for ${row.primaryid}${row.yearserved ? " (" + row.yearserved + ")" : ""}?`,
            success: null,
        });
        setPendingAction(() => async () => {
            await confirmDeleteLegacyRow(row);
        });
    };

    const confirmDeleteLegacyRow = async (row) => {
        try {
            setChairBusy(true);
            const res = await fetch(`${API_BASE}/api/legacy/${row.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setChairMsg(data?.error || `Delete failed (${res.status})`);
                setMessageBox({
                    show: true,
                    text: data?.error || `❌ Delete failed (${res.status}).`,
                    success: false,
                });
                return;
            }
            await loadList({ pageIndex: 0 });
            setChairMsg("Legacy entry deleted.");
            setMessageBox({
                show: true,
                text: "✅ Legacy entry deleted.",
                success: true,
            });
        } catch {
            setChairMsg("Network error while deleting.");
            setMessageBox({
                show: true,
                text: "❌ Network error while deleting.",
                success: false,
            });
        } finally {
            setChairBusy(false);
            setPendingAction(null);
        }
    };

    /* ================= UPLOAD / REMOVE PHOTO for CHAIRMAN ===================== */
    const uploadChairmanPhoto = async (primaryid, file) => {
        if (!file || !primaryid) return alert("Missing file or ID");

        try {
            const formData = new FormData();
            formData.append("photo", file);

            const token = getToken();
            const res = await fetch(`${API_BASE}/api/legacy/upload-photo/${primaryid}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error("Server returned invalid response (HTML instead of JSON)");
            }

            if (!res.ok || !data.ok) throw new Error(data?.error || "Upload failed");

            // 🔥 SUCCESS POPUP + AUTO-HIDE
            setMessageBox({ show: true, text: "✅ Photo uploaded successfully!", success: true });

            // 🔥 Update displayed image
            setCurrentPhotoUrl(`${API_BASE}/api/legacy/photo/${encodeURIComponent(primaryid)}?t=${Date.now()}`);

            console.log("📸 File URL:", data.url);

        } catch (err) {
            console.error("Photo upload error:", err);

            // ❌ ERROR POPUP + AUTO-HIDE
            setMessageBox({ show: true, text: "❌ Network error during upload.", success: false });
        }
    };
    useEffect(() => {
        if (messageBox.show && messageBox.success !== null) {
            const timer = setTimeout(() => {
                setMessageBox({ show: false, text: "", success: false });
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [messageBox.show, messageBox.success]);

    // ===================== UPLOAD CROPPED PHOTO =====================
    const handleCroppedUpload = async (blob) => {
        if (!blob || !cropPrimaryID || !cropGender) return;

        setUploading(true);

        try {
            const formData = new FormData();
            formData.append("photo", blob);

            const res = await fetch(
                `${API_BASE}/api/members/upload-photo/${cropGender.toLowerCase()}/${cropPrimaryID}`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${getToken()}` },
                    body: formData,
                }
            );

            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data.error || "Upload failed");

            // refresh image display
            setCurrentPhotoUrl(
                `${API_BASE}/UploadPhoto/${cropGender}-Photo/${cropPrimaryID}.jpg?${Date.now()}`
            );

            setMessageBox({
                show: true,
                text: `✅ ${cropGender} photo uploaded successfully!`,
                success: true,
            });
        } catch (err) {
            console.error(err);
            setMessageBox({
                show: true,
                text: "❌ Failed to upload cropped photo.",
                success: false,
            });
        } finally {
            setUploading(false);
            setCropModalOpen(false);
        }
    };

    /* ---------------- REMOVE CHAIRMAN PHOTO ---------------- */
    // ===================== REMOVE CHAIRMAN PHOTO =====================
    const removeChairmanPhoto = async (primaryid) => {
        if (!primaryid) return alert("No Primary ID found.");

        // ✅ Show confirmation box — do NOT auto-run deletion yet
        setMessageBox({
            show: true,
            text: `❓ Remove Chairman photo for ${primaryid}?`,
            success: null, // null = "confirmation" mode
        });

        // ✅ When OK clicked, confirmRemoveChairmanPhoto will execute
        setPendingAction(() => () => confirmRemoveChairmanPhoto(primaryid));
    };

    // ===================== CONFIRM (AFTER OK CLICK) =====================
    const confirmRemoveChairmanPhoto = async (primaryid) => {
        try {
            const res = await fetch(`${API_BASE}/api/chairman/remove-photo/${primaryid}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });

            const data = await res.json();

            if (res.ok && data.ok) {
                setMessageBox({
                    show: true,
                    text: "✅ Chairman photo removed successfully!",
                    success: true,
                });
                setCurrentPhotoUrl("/default-placeholder.png");
            } else {
                setMessageBox({
                    show: true,
                    text: data.error || "❌ Failed to remove photo.",
                    success: false,
                });
            }
        } catch (err) {
            console.error("Remove error:", err);
            setMessageBox({
                show: true,
                text: "❌ Network error while removing photo.",
                success: false,
            });
        } finally {
            // ✅ Only auto-hide after the delete actually completes
            setPendingAction(null);
        }
    };

    /* ===================== UPLOAD / REMOVE PHOTO for PHI & SIGMA ===================== */
    /* eslint-disable no-unused-vars */
    const uploadMemberPhoto = async (gender, primaryid, file) => {
        if (!file || !primaryid) return alert("Missing file or ID");
        const lower = gender.toLowerCase();

        try {
            const formData = new FormData();
            formData.append("photo", file);
            const token = getToken();

            const res = await fetch(`${API_BASE}/api/members/upload-photo/${lower}/${primaryid}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error("Server returned invalid response (HTML instead of JSON)");
            }

            if (!res.ok || !data.ok) throw new Error(data?.error || "Upload failed");

            // ✅ Show success message
            setMessageBox({ show: true, text: `✅ ${gender} photo uploaded successfully!`, success: true });

            // ✅ Update the image state to force-refresh once
            setCurrentPhotoUrl(`${API_BASE}/UploadPhoto/${gender}-Photo/${primaryid}.jpg?${Date.now()}`);
        } catch (err) {
            console.error("Photo upload error:", err);
            setMessageBox({ show: true, text: "❌ Network error during upload.", success: false });
        }
    };

    /* ---------------- REMOVE MEMBER PHOTO ---------------- */
    const removeMemberPhoto = async (gender, primaryid) => {
        if (!primaryid) return alert("No Primary ID found.");
        const lower = gender.toLowerCase();

        setMessageBox({
            show: true,
            text: `❓ Remove ${gender} photo for ${primaryid}?`,
            success: null,
        });
        setPendingAction(() => async () => {
            await confirmRemoveMemberPhoto(lower, primaryid);
        });
    };
    /* ===================== CONFIRM (AFTER OK CLICK) ===================== */
    const confirmRemoveMemberPhoto = async (gender, primaryid) => {
        try {
            const res = await fetch(`${API_BASE}/api/members/remove-photo/${gender}/${primaryid}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setMessageBox({ show: true, text: `✅ ${gender} photo removed.`, success: true });
                document.querySelectorAll("img").forEach((img) => {
                    if (img.src.includes(primaryid)) img.src = "/default-placeholder.png";
                });
            } else {
                setMessageBox({
                    show: true,
                    text: data.error || "❌ Failed to remove photo.",
                    success: false,
                });
            }
        } catch (err) {
            console.error(err);
            setMessageBox({
                show: true,
                text: "❌ Network error while removing photo.",
                success: false,
            });
        } finally {
            setPendingAction(null);
        }
    };

    /* ================= ARCHIVED ===================== */
    /* ---------------- LOAD ARCHIVED MEMBERS ---------------- */
    const loadArchiveList = async () => {
        setArchiveError("");
        try {
            const res = await fetch(`${API_BASE}/api/archive`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setArchiveRows(data.rows || []);
                setArchiveTotal(data.total || 0);
            } else {
                setArchiveError(data.error || "Failed to load archive list.");
            }
        } catch (err) {
            setArchiveError("Network error while loading archive list.");
        }
    };

    /* ---------------- ACTIVATE ARCHIVED MEMBER ---------------- */
    const activateMember = async (row) => {
        if (!window.confirm(`Activate ${row.primaryid} (${row.lastname}, ${row.firstname})?`)) return;
        try {
            const res = await fetch(`${API_BASE}/api/archive/activate/${row.id}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                alert("✅ Member reactivated successfully!");
                loadArchiveList();
            } else {
                alert(data.error || "❌ Activation failed.");
            }
        } catch {
            alert("❌ Network error during activation.");
        }
    };

    /* ---------------- PERMANENTLY DELETE ARCHIVED MEMBER ---------------- */
    const deleteArchiveMember = (row) => {
        // ✅ Ask for confirmation via your messageBox
        setMessageBox({
            show: true,
            text: `❓ Permanently delete ${row.primaryid} (${row.lastname}, ${row.firstname})? This action cannot be undone.`,
            success: null, // null = "confirmation" mode
        });

        // ✅ If user clicks OK, run this later
        setPendingAction(() => async () => {
            try {
                const res = await fetch(`${API_BASE}/api/archive/delete/${row.id}`, {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const data = await res.json();
                if (res.ok && data.ok) {
                    setMessageBox({
                        show: true,
                        text: "🗑️ Archived member permanently deleted.",
                        success: true,
                    });
                    loadArchiveList();
                } else {
                    setMessageBox({
                        show: true,
                        text: data.error || "❌ Failed to delete permanently.",
                        success: false,
                    });
                }
            } catch (err) {
                console.error("Delete archive error:", err);
                setMessageBox({
                    show: true,
                    text: "❌ Network error during permanent delete.",
                    success: false,
                });
            } finally {
                setPendingAction(null);
            }
        });
    };

    /* eslint-enable no-unused-vars */
    /* ================= TOGGLE SORT ================= */
    const toggleSort = (col) => {
        if (orderBy === col) {
            // if already sorting by this col → flip ASC/DESC
            setOrderDir(orderDir === "asc" ? "desc" : "asc");
        } else {
            // if new column → set ASC
            setOrderBy(col);
            setOrderDir("asc");
        }
    };

    // 📄 Archive table search + sort + pagination
    const [archiveSearch, setArchiveSearch] = useState("");
    const [archivePage, setArchivePage] = useState(1);
    const [archiveRowsPerPage, setArchiveRowsPerPage] = useState(10);

    // 🔽 Sorting state
    const [archiveOrderBy, setArchiveOrderBy] = useState("archived_at");
    const [archiveOrderDir, setArchiveOrderDir] = useState("desc");

    // 🔁 Toggle sort
    const toggleArchiveSort = (col) => {
        if (archiveOrderBy === col) {
            setArchiveOrderDir(archiveOrderDir === "asc" ? "desc" : "asc");
        } else {
            setArchiveOrderBy(col);
            setArchiveOrderDir("asc");
        }
    };

    const loadSecurityUsers = async () => {
        setSecurityLoading(true);
        setSecurityMsg("");

        try {
            const res = await fetch(`${API_BASE}/api/security/users`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setSecurityMsg(data?.error || "Failed to load users.");
                return;
            }

            setSecurityRows(data.rows || []);
            setCanManageSecurityRoles(Boolean(data.can_manage_roles));
            setSecurityPage(1);
        } catch {
            setSecurityMsg("Network error while loading security users.");
        } finally {
            setSecurityLoading(false);
        }
    };

    const updateSecurityRole = async (idname, role) => {
        setSecurityMsg("");
        setSecurityRoleBusy(idname);

        try {
            const res = await fetch(
                `${API_BASE}/api/security/users/${encodeURIComponent(idname)}/role`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`,
                    },
                    body: JSON.stringify({ role }),
                }
            );
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setSecurityMsg(data?.error || "Role update failed.");
                return;
            }

            setSecurityRows((prev) =>
                prev.map((row) =>
                    row.idname === idname ? { ...row, role: data.row.role } : row
                )
            );
            setSecurityMsg(`${idname} role updated to ${data.row.role}.`);
        } catch {
            setSecurityMsg("Network error while updating role.");
        } finally {
            setSecurityRoleBusy("");
        }
    };

    const resetSecurityPassword = async (idname) => {
        if (!window.confirm(`Reset password for ${idname} to default?`)) return;

        setSecurityMsg("");
        try {
            const res = await fetch(
                `${API_BASE}/api/security/users/${encodeURIComponent(idname)}/reset-password`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${getToken()}` },
                }
            );
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setSecurityMsg(data?.error || "Password reset failed.");
                return;
            }

            setSecurityMsg(`Password reset for ${idname}. User must create a new password on next login.`);
            await loadSecurityUsers();
        } catch {
            setSecurityMsg("Network error while resetting password.");
        }
    };

    useEffect(() => {
        if (view === "security") {
            loadSecurityUsers();
        }
    }, [view]);

    // 🔍 Search filter
    useEffect(() => {
        if (!securityMsg) return undefined;

        const timer = setTimeout(() => {
            setSecurityMsg("");
        }, 3000);

        return () => clearTimeout(timer);
    }, [securityMsg]);

    const filteredArchive = useMemo(() => {
        const q = archiveSearch.toLowerCase();
        return archiveRows.filter((item) =>
            item.primaryid?.toLowerCase().includes(q) ||
            item.firstname?.toLowerCase().includes(q) ||
            item.lastname?.toLowerCase().includes(q)
        );
    }, [archiveRows, archiveSearch]);

    const filteredSecurityRows = useMemo(() => {
        const q = securitySearch.toLowerCase();
        return securityRows.filter((row) =>
            (securityPrimaryFilter === "all" ||
                row.primaryid?.toUpperCase().startsWith(`${securityPrimaryFilter}-`)) &&
            [
                row.idname,
                row.primaryid,
                row.firstname,
                row.lastname,
                row.email,
                row.role,
            ]
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [securityRows, securitySearch, securityPrimaryFilter]);

    const sortedSecurityRows = useMemo(() => {
        return [...filteredSecurityRows].sort((a, b) => {
            const first = String(a[securitySort.key] || "");
            const second = String(b[securitySort.key] || "");
            const comparison = first.localeCompare(second, undefined, { sensitivity: "base", numeric: true });
            return securitySort.direction === "asc" ? comparison : -comparison;
        });
    }, [filteredSecurityRows, securitySort]);

    const toggleSecuritySort = (key) => {
        setSecuritySort((current) => ({
            key,
            direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
        }));
        setSecurityPage(1);
    };

    useEffect(() => {
        setSecurityPage(1);
    }, [securitySearch, securityRowsPerPage, securityPrimaryFilter]);

    const totalSecurityPages = Math.ceil(filteredSecurityRows.length / securityRowsPerPage);
    const visibleSecurityRows = sortedSecurityRows.slice(
        (securityPage - 1) * securityRowsPerPage,
        securityPage * securityRowsPerPage
    );

    // 🔽 Sorting
    const sortedArchive = useMemo(() => {
        return [...filteredArchive].sort((a, b) => {
            let aVal = a[archiveOrderBy] ?? "";
            let bVal = b[archiveOrderBy] ?? "";

            // Date sorting
            if (archiveOrderBy === "archived_at") {
                return archiveOrderDir === "asc"
                    ? new Date(aVal) - new Date(bVal)
                    : new Date(bVal) - new Date(aVal);
            }

            // String sorting
            return archiveOrderDir === "asc"
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        });
    }, [filteredArchive, archiveOrderBy, archiveOrderDir]);

    // 📄 Pagination
    const totalArchivePages = Math.ceil(sortedArchive.length / archiveRowsPerPage);

    const startIndex = (archivePage - 1) * archiveRowsPerPage;

    const visibleArchiveRows = sortedArchive.slice(
        startIndex,
        startIndex + archiveRowsPerPage
    );


    /* ---------------- RENDER ---------------- */
    return (
        <section className={`member-content admin-view-${view}`}>
            {/* Header with toggle */}
            <div
                className="member-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: "10px",
                }}
            >
                <button
                    className={`admin-mobile-side-nav-toggle ${mobileNavOpen ? "open" : ""} ${mobileNavTooltipVisible ? "tooltip-visible" : ""}`}
                    type="button"
                    onClick={handleMobileNavToggle}
                    aria-expanded={mobileNavOpen}
                    aria-controls="admin-mobile-side-nav"
                    aria-label={mobileNavOpen ? "Hide navigation" : "Show navigation"}
                    data-tooltip={mobileNavOpen ? "Close Navigation" : "Open Navigation"}
                >
                    Navigation
                </button>

                {mobileNavOpen && (
                    <button
                        className="admin-mobile-side-nav-scrim"
                        type="button"
                        aria-label="Close navigation"
                        onClick={() => setMobileNavOpen(false)}
                    />
                )}

                <aside
                    id="admin-mobile-side-nav"
                    className={`admin-mobile-side-nav ${mobileNavOpen ? "open" : ""}`}
                    aria-hidden={!mobileNavOpen}
                >
                    <div className="admin-mobile-side-profile">
                        <img
                            className="admin-mobile-avatar"
                            src={adminDrawerPhoto}
                            onError={(e) => (e.currentTarget.src = adminDrawerPlaceholder)}
                            alt=""
                        />
                        <div>
                            <strong>{adminDrawerName}</strong>
                            <span>{adminDrawerRole}</span>
                        </div>
                    </div>

                    <div className="admin-mobile-side-links">
                        {onPreview && (
                            <button
                                className={`admin-mobile-side-link preview-link ${mobilePreviewTooltipVisible ? "tooltip-visible" : ""}`}
                                onClick={handleMobilePreviewClick}
                                type="button"
                                data-tooltip="Go to User Account"
                            >
                                Preview
                            </button>
                        )}
                        <button className={`admin-mobile-side-link ${view === "profile" ? "active" : ""}`} onClick={openMobileProfile} type="button">
                            My Members
                        </button>
                        {!isComelec && (
                            <>
                                <button className={`admin-mobile-side-link ${view === "report" ? "active" : ""}`} onClick={() => openMobileAdminView("report")} type="button">
                                    Reports
                                </button>
                                <button className={`admin-mobile-side-link ${view === "calendar" ? "active" : ""}`} onClick={() => openMobileAdminView("calendar")} type="button">
                                    Calendar
                                </button>
                                <button className={`admin-mobile-side-link ${view === "security" ? "active" : ""}`} onClick={() => openMobileAdminView("security")} type="button">
                                    Security
                                </button>
                            </>
                        )}
                        <div className={`admin-mobile-ovs-group ${mobileOvsOpen ? "open" : ""}`}>
                            <button
                                className={`admin-mobile-side-link admin-mobile-ovs-toggle ${
                                    view === "voting" || view === "finishedVoting" || view === "ovsPassword" ? "active" : ""
                                }`}
                                onClick={() => setMobileOvsOpen((open) => !open)}
                                type="button"
                                aria-expanded={mobileOvsOpen}
                            >
                                OVS <span aria-hidden="true">▼</span>
                            </button>
                            <div className="admin-mobile-ovs-options">
                                <button type="button" onClick={() => openMobileAdminView("voting")}>
                                    New Election
                                </button>
                                <button type="button" onClick={() => openMobileAdminView("finishedVoting")}>
                                    Previous Election
                                </button>
                                {canEditOvsPassword && (
                                    <button type="button" onClick={openOvsPasswordView}>
                                        Edit Password
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <button className="admin-mobile-side-logout" onClick={handleLogout} type="button">
                        Logout
                    </button>
                </aside>

                <div className="header-buttons">
                    <button
                        className={`header-btn ${view === "profile" ? "active" : ""}`}
                        onClick={() => {
                            setView("profile");
                            localStorage.removeItem("admin_last_view"); // 🧹 clear saved view
                        }}
                    >
                        My Members
                    </button>

                    <button
                        className={`header-btn report-nav-btn ${isComelec ? "restricted-hidden" : ""} ${view === "report" ? "active" : ""}`}
                        onClick={() => {
                            setView("report");
                            localStorage.setItem("admin_last_view", "report");  // 🔥 KEEP REPORT
                        }}
                    >
                        Report
                    </button>

                    {!isComelec && (
                        <div
                            className="ovs-dropdown"
                            ref={ovsDropdownRef}
                            onMouseEnter={openOvsMenu}
                            onMouseLeave={closeOvsMenuSoon}
                        >
                            <button
                                className={`header-btn ovs-dropdown-btn ${
                                    view === "voting" || view === "finishedVoting" || view === "ovsPassword" ? "active" : ""
                                }`}
                                data-tooltip="Online Voting System"
                                onClick={() => setOvsMenuOpen((open) => !open)}
                                type="button"
                            >
                                OVS <span className="ovs-caret">▼</span>
                            </button>

                            <div className={`ovs-dropdown-menu ${ovsMenuOpen ? "open" : ""}`}>
                                <button type="button" onClick={() => openVotingView("voting")}>
                                    New Election
                                </button>
                                <button type="button" onClick={() => openVotingView("finishedVoting")}>
                                    Previous Election
                                </button>
                                {canEditOvsPassword && (
                                    <button type="button" onClick={openOvsPasswordView}>
                                        Edit Password
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <button
                        className={`header-btn ${!isComelec ? "restricted-hidden" : ""} ${view === "voting" ? "active" : ""}`}
                        data-tooltip="Online Voting System"
                        onClick={() => {
                            // 🔥 Tell AdminVoting.jsx that the user intentionally opened OVS
                            sessionStorage.setItem("openOVS", "yes");

                            setView("voting");
                            localStorage.setItem("admin_last_view", "voting");
                        }}
                    >
                        OVS
                    </button>

                    <button
                        className={`header-btn ${!isComelec ? "restricted-hidden" : ""} ${view === "finishedVoting" ? "active" : ""}`}
                        onClick={() => {
                            sessionStorage.setItem("openOVS", "yes");
                            setView("finishedVoting");
                            localStorage.setItem("admin_last_view", "finishedVoting");
                        }}
                    >
                        Previous Election
                    </button>

                    {canEditOvsPassword && (
                        <button
                            className={`header-btn ${!isComelec ? "restricted-hidden" : ""} ${view === "ovsPassword" ? "active" : ""}`}
                            onClick={openOvsPasswordView}
                        >
                            Edit Password
                        </button>
                    )}

                    <button
                        className={`header-btn ${isComelec ? "restricted-hidden" : ""} ${view === "calendar" ? "active" : ""}`}
                        onClick={() => {
                            setView("calendar");
                            localStorage.setItem("admin_last_view", "calendar");
                        }}
                    >
                        Calendar
                    </button>

                    <button
                        className={`header-btn ${isComelec ? "restricted-hidden" : ""} ${view === "security" ? "active" : ""}`}
                        onClick={() => {
                            setView("security");
                            localStorage.setItem("admin_last_view", "security");
                        }}
                    >
                        Security
                    </button>
                </div>

                {onPreview && (
                    <button
                        className="preview-btn"
                        onClick={onPreview}
                        type="button"
                        data-tooltip="Go to User Account"
                    >
                        Preview
                    </button>
                )}

                <button
                    className="logout-btn"
                    onClick={handleLogout}
                    type="button"
                >
                    Logout
                </button>
            </div>


            {/* ================= PROFILE VIEW ================= */}
            {view === "profile" && (
                <>
                    {/* Tabs */}
                    <div className="tabs admin-profile-tabs">
                        {["Phi", "Sigma", "Chairman", "Archive"].map((tab) => (
                            <button
                                key={tab}
                                className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                                onClick={() => {
                                    setActiveTab(tab);
                                    setShowAddMemberForm(false);
                                    setShowChairmanEntryForm(false);
                                    localStorage.setItem("admin_profile_tab", tab);
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* ---------- Phi/Sigma (Add Member + Table + Modal) ---------- */}
                    {activeTab === "Phi" || activeTab === "Sigma" ? (
                        <>
                            {/* 🔹 Add Member Form */}
                            <div className={`profile-card add-member-panel ${showAddMemberForm ? "" : "add-member-collapsed"}`}>
                                <div className="add-member-panel-head">
                                    <h3>{activeTab} — Add Member</h3>
                                    <button
                                        type="button"
                                        className="btn-primary add-member-toggle-btn"
                                        onClick={() => setShowAddMemberForm((open) => !open)}
                                        aria-expanded={showAddMemberForm}
                                    >
                                        {showAddMemberForm ? "Hide" : "Add"}
                                    </button>
                                </div>
                                <div className="grid">
                                    {addMemberFields.map((key) => (
                                        <div className="field" key={key}>
                                            <label>{labelMap[key]}</label>
                                            <input
                                                type={key === "email" ? "email" : key === "mobile" ? "tel" : "text"}
                                                name={key}
                                                value={currentForm[key] || ""}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="card-actions">
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="btn-primary"
                                    >
                                        {saving ? "Saving…" : "Save"}
                                    </button>
                                    <button
                                        className="btn-secondary"
                                        onClick={() => setCurrentForm({ ...emptyProfile })}
                                    >
                                        Clear
                                    </button>
                                </div>

                                {saveMsg && (
                                    <div
                                        style={{
                                            marginTop: 10,
                                            fontWeight: 600,
                                            color: /saved|updated|deleted/i.test(saveMsg)
                                                ? "#5ad67d"
                                                : "#ff6b6b",
                                        }}
                                    >
                                        {saveMsg}
                                    </div>
                                )}
                            </div>

                            {/* 🔹 Members Table */}
                            <div
                                className="member-table"
                                style={{
                                    marginTop: 24,
                                    width: "100%",
                                    background: "#fff",
                                    borderRadius: "8px",
                                    padding: "10px"
                                }}
                            >
                                <div
                                    className="table-controls"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",   // ✔ keep in one row
                                        width: "100%",
                                        flexWrap: "nowrap",                // ✔ prevent wrapping
                                        gap: "10px",
                                        marginBottom: "10px",
                                    }}
                                >
                                    <div>
                                        <select
                                            value={pageLen}
                                            onChange={(e) => setPageLen(Number(e.target.value))}
                                            style={{ padding: "6px 8px", borderRadius: "6px" }}
                                        >
                                            <option value={10}>10 | Show entries</option>
                                            <option value={25}>25 | Show entries</option>
                                            <option value={50}>50 | Show entries</option>
                                            <option value={100}>100 | Show entries</option>
                                            <option value={250}>250 | Show entries</option>
                                        </select>
                                    </div>

                                    <div
                                        className="total-count"
                                        style={{
                                            flex: 1,
                                            textAlign: "center",
                                            fontWeight: 600,
                                            color: "darkgoldenrod",
                                        }}
                                    >
                                        {activeTab === "Phi" && `Total Phi Members: ${total}`}
                                        {activeTab === "Sigma" && `Total Sigma Members: ${total}`}
                                    </div>

                                    <div>
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search ID, Name, Root Chapter…"
                                            style={{
                                                padding: "6px 8px",
                                                borderRadius: 6,
                                                border: "1px solid #bbb",
                                                color: "#333",
                                                background: "#fff",
                                            }}
                                        />
                                    </div>
                                </div>

                                {tableError && (
                                    <div style={{ color: "#ff7676", marginTop: 8 }}>{tableError}</div>
                                )}

                                {/* Table */}

                                {/* 🔹 Export Button */}
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        marginBottom: "8px",
                                    }}
                                >
                                    <button
                                        className="export-btn"
                                        onClick={() =>
                                            exportCSV(rows, `${activeTab.toLowerCase()}_members`)
                                        }
                                    >
                                        📤 Export CSV
                                    </button>
                                </div>

                                {/* 🔹 Table Wrapper */}
                                <div
                                    className="table-wrap"
                                    style={{
                                        overflowX: "auto",
                                        marginTop: 10,
                                        background: "#fff",
                                        borderRadius: "8px",
                                        padding: "10px",
                                    }}
                                >
                                    <table className="nice-table light-table" style={{ width: "100%" }}>
                                        <thead>
                                            <tr>
                                                {[
                                                    { key: "primaryid", label: "Primary ID" },
                                                    { key: "lastname", label: "Last Name" },
                                                    { key: "firstname", label: "First Name" },
                                                    { key: "alias", label: "Alias" },
                                                    { key: "rootchapter", label: "Root Chapter" },
                                                    { key: "birthsurvive", label: "Birth/Survive" },
                                                    { key: "domname", label: "Verified By" },
                                                    { key: "_actions", label: "Actions" },
                                                ].map((col) => (
                                                    <th
                                                        key={col.key}
                                                        onClick={() =>
                                                            col.key !== "_actions" && toggleSort(col.key)
                                                        }
                                                        className="light-th"
                                                        style={{
                                                            cursor:
                                                                col.key !== "_actions"
                                                                    ? "pointer"
                                                                    : "default",
                                                        }}
                                                    >
                                                        {col.label}
                                                        {orderBy === col.key
                                                            ? orderDir === "asc"
                                                                ? " ▲"
                                                                : " ▼"
                                                            : ""}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>


                                        <tbody>
                                            {rows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="light-td center">
                                                        No records found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                rows.map((r) => (
                                                    <tr key={r.id ?? r.primaryid} className="light-tr">
                                                        <td className="light-td">{r.primaryid}</td>
                                                        <td className="light-td">{r.lastname}</td>
                                                        <td className="light-td">{r.firstname}</td>
                                                        <td className="light-td">{r.alias || "—"}</td>
                                                        <td className="light-td">{r.rootchapter || "—"}</td>
                                                        <td className="light-td">{r.birthsurvive || "—"}</td>
                                                        <td className="light-td">{r.domname || "—"}</td>
                                                        <td
                                                            className="light-td"
                                                            style={{ whiteSpace: "nowrap" }}
                                                        >
                                                            <button
                                                                className="btn-primary"
                                                                style={{ marginRight: "6px" }}
                                                                onClick={() => openMemberEdit(r)}
                                                            >
                                                                Edit
                                                            </button>

                                                            <button
                                                                className="btn-secondary"
                                                                onClick={() => deleteMember(r)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* 🔹 Pager ⬅ NOW INSIDE THE WHITE BOX */}
                                <div
                                    className="table-footer"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        marginTop: 10,
                                    }}
                                >
                                    <div className="muted">
                                        Showing{" "}
                                        <strong>{rows.length ? page * pageLen + 1 : 0}</strong> to{" "}
                                        <strong>{Math.min((page + 1) * pageLen, total)}</strong> of{" "}
                                        <strong>{total}</strong> entries ({activeTab})
                                    </div>

                                    <div
                                        style={{
                                            marginLeft: "auto",
                                            display: "flex",
                                            gap: 2,
                                        }}
                                    >
                                        <button
                                            disabled={page === 0}
                                            onClick={() => loadList({ pageIndex: Math.max(page - 1, 0) })}
                                            className="btn-secondary"
                                        >
                                            Prev
                                        </button>

                                        <span className="pager-badge">
                                            Page {page + 1} / {Math.max(Math.ceil(total / pageLen), 1)}
                                        </span>

                                        <button
                                            disabled={(page + 1) * pageLen >= total}
                                            onClick={() =>
                                                loadList({
                                                    pageIndex: Math.min(
                                                        page + 1,
                                                        Math.max(Math.ceil(total / pageLen), 1) - 1
                                                    ),
                                                })
                                            }
                                            className="btn-secondary"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>

                            </div>


                            {/* 🔹 Edit Modal */}
                            {editMemberRow && (
                                <div className="modal-overlay" onClick={closeMemberEdit}>
                                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                                        <button className="close-button" onClick={closeMemberEdit}>✕</button>
                                        <h3>Edit Member {editMemberRow.primaryid}</h3>

                                        <div className="modal-grid">
                                            {editMemberFields.map((key) => (
                                                <div className="field" key={key}>
                                                    <label>{labelMap[key]}</label>
                                                    <input
                                                        name={key}
                                                        type={key === "email" ? "email" : key === "mobile" ? "tel" : "text"}
                                                        value={editMemberData[key] || ""}
                                                        onChange={(e) =>
                                                            setEditMemberData((prev) => ({
                                                                ...prev,
                                                                [e.target.name]: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        {/* ✅ Upload / Remove Photo for Phi or Sigma */}
                                        {editMemberRow?.primaryid && (
                                            <div className="upload-photo-section">
                                                <label><strong>Upload {activeTab} Photo (JPG/PNG)</strong></label>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        if (e.target.files?.[0]) {
                                                            setCropImageFile(e.target.files[0]);
                                                            setCropPrimaryID(editMemberRow.primaryid);
                                                            setCropGender(activeTab);
                                                            setCropModalOpen(true);
                                                        }
                                                    }}
                                                />

                                                <img
                                                    src={currentPhotoUrl || `${API_BASE}/UploadPhoto/${activeTab}-Photo/${editMemberRow.primaryid}.jpg`}
                                                    alt={`${activeTab} member`}
                                                    onError={(e) => (e.currentTarget.src = "/default-placeholder.png")}
                                                />

                                                <button
                                                    className="remove-btn"
                                                    onClick={() => removeMemberPhoto(activeTab, editMemberRow.primaryid)}
                                                >
                                                    Remove Photo
                                                </button>
                                            </div>
                                        )}


                                        <div className="card-actions" style={{ marginTop: 20 }}>
                                            <button
                                                className="btn-primary"
                                                onClick={saveMemberEdit}
                                                disabled={saving}
                                            >
                                                {saving ? "Saving…" : "Save"}
                                            </button>
                                            <button className="btn-secondary" onClick={closeMemberEdit}>
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : null}

                    {/* ---------- Chairman tab ---------- */}
                    {activeTab === "Chairman" && (
                        <>
                            {/* 🔹 Chairman Legacy Form */}
                            <div className={`profile-card chairman-entry-panel ${showChairmanEntryForm ? "" : "chairman-entry-collapsed"}`}>
                                <div className="add-member-panel-head">
                                    <h3 className="card-title">Chairman — Legacy Entry</h3>
                                    <button
                                        type="button"
                                        className="btn-primary add-member-toggle-btn"
                                        onClick={() => setShowChairmanEntryForm((open) => !open)}
                                        aria-expanded={showChairmanEntryForm}
                                    >
                                        {showChairmanEntryForm ? "Hide" : "Add"}
                                    </button>
                                </div>

                                {/* Member Search */}
                                <div className="field-row">
                                    <div className="field id-field">
                                        <label>Search Member</label>
                                        <input
                                            placeholder="Primary ID, first name, or last name"
                                            value={chairPID}
                                            onChange={(e) => {
                                                setChairPID(e.target.value);
                                                setChairFound(null);
                                                setChairMsg("");
                                            }}
                                            onFocus={() => setChairSearchOpen(true)}
                                        />
                                        {chairSearchOpen && chairMatches.length > 0 && (
                                            <div className="chairman-search-results">
                                                {chairMatches.map((member) => (
                                                    <button
                                                        type="button"
                                                        key={`${member.source}-${member.primaryid}`}
                                                        className="chairman-search-option"
                                                        onClick={() => selectChairmanMatch(member)}
                                                    >
                                                        <span>
                                                            <strong>{member.primaryid}</strong>
                                                            {" - "}
                                                            {member.firstname} {member.lastname}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Lookup results */}
                                <div className="grid" style={{ marginTop: 12 }}>
                                    <div className="field">
                                        <label>Primary ID</label>
                                        <input value={chairFound?.primaryid || ""} readOnly />
                                    </div>
                                    <div className="field">
                                        <label>Last Name</label>
                                        <input value={chairFound?.lastname || ""} readOnly />
                                    </div>
                                    <div className="field">
                                        <label>First Name</label>
                                        <input value={chairFound?.firstname || ""} readOnly />
                                    </div>
                                    <div className="field">
                                        <label>Alias</label>
                                        <input value={chairFound?.alias || ""} readOnly />
                                    </div>
                                    <div className="field">
                                        <label>Year Served</label>
                                        <input
                                            placeholder="Enter the Year"
                                            value={chairYear}
                                            onChange={(e) => setChairYear(e.target.value)}
                                            maxLength={15}
                                        />
                                    </div>
                                </div>

                                {/* Message status */}
                                {chairMsg && (
                                    <div
                                        style={{
                                            marginTop: 10,
                                            color: /(saved|updated|deleted|successfully)/i.test(chairMsg)
                                                ? "#5ad67d"
                                                : /not found/i.test(chairMsg)
                                                    ? "orange"
                                                    : "#ff6b6b",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {chairMsg}
                                    </div>
                                )}

                                <div className="card-actions">
                                    <button
                                        className="btn-primary"
                                        onClick={saveChairman}
                                        disabled={chairBusy || !chairFound}
                                    >
                                        {chairBusy ? "Saving…" : "Save"}
                                    </button>
                                    <button
                                        className="btn-secondary"
                                        onClick={() => {
                                            setChairPID("");
                                            setChairYear("");
                                            setChairFound(null);
                                            setChairMsg("");
                                            setChairMatches([]);
                                            setChairSearchOpen(false);
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            {/* 🔹 Legacy Table */}
                            <div
                                className="member-table"
                                style={{
                                    marginTop: 24,
                                    width: "100%",
                                    background: "#fff",
                                    borderRadius: "8px",
                                    padding: "10px"
                                }}
                            >

                                {/* Controls */}
                                <div
                                    className="table-controls"
                                    style={{
                                        display: "flex",
                                        gap: 12,
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <div>
                                        <select
                                            value={pageLen}
                                            onChange={(e) => setPageLen(Number(e.target.value))}
                                            style={{ padding: "6px 8px", borderRadius: "6px" }}
                                        >
                                            <option value={10}>10 | Show entries</option>
                                            <option value={25}>25 | Show entries</option>
                                            <option value={50}>50 | Show entries</option>
                                            <option value={100}>100 | Show entries</option>
                                        </select>
                                    </div>

                                    {/* Right Search */}
                                    <div style={{ marginLeft: "auto" }}>
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search ID, Name, Year…"
                                            style={{
                                                padding: "6px 8px",
                                                borderRadius: 6,
                                                border: "1px solid #bbb",
                                                color: "#333",
                                                background: "#fff",
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* 🔹 Export Button */}
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        marginBottom: "8px",
                                    }}
                                >
                                    <button
                                        className="export-btn"
                                        onClick={() => exportCSV(rows, "chairman_legacy")}
                                    >
                                        📤 Export CSV
                                    </button>
                                </div>

                                {/* 🔹 Table Container */}
                                <div
                                    className="table-wrap"
                                    style={{
                                        overflowX: "auto",
                                        marginTop: 10,
                                        background: "#fff",
                                        borderRadius: "8px",
                                        padding: "10px",
                                    }}
                                >
                                    <table
                                        className="nice-table light-table"
                                        style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            tableLayout: "auto",
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                {[
                                                    { key: "primaryid", label: "Primary ID" },
                                                    { key: "lastname", label: "Last Name" },
                                                    { key: "firstname", label: "First Name" },
                                                    { key: "alias", label: "Alias" },
                                                    { key: "yearserved", label: "Year Served" },
                                                    { key: "rootchapter", label: "Chapter" },
                                                    { key: "_actions", label: "Actions" },
                                                ].map((col) => (
                                                    <th
                                                        key={col.key}
                                                        onClick={() =>
                                                            col.key !== "_actions" && toggleSort(col.key)
                                                        }
                                                        className="light-th"
                                                        style={{
                                                            cursor:
                                                                col.key !== "_actions"
                                                                    ? "pointer"
                                                                    : "default",
                                                        }}
                                                    >
                                                        {col.label}
                                                        {orderBy === col.key
                                                            ? orderDir === "asc"
                                                                ? " ▲"
                                                                : " ▼"
                                                            : ""}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>




                                        <tbody>
                                            {rows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="light-td center">
                                                        No records found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                rows.map((r) => (
                                                    <tr
                                                        key={
                                                            r.id ??
                                                            `${r.primaryid}-${r.yearserved || ""}`
                                                        }
                                                        className="light-tr"
                                                    >
                                                        <td className="light-td">{r.primaryid}</td>
                                                        <td className="light-td">{r.lastname}</td>
                                                        <td className="light-td">{r.firstname}</td>
                                                        <td className="light-td">{r.alias || "—"}</td>
                                                        <td className="light-td">{r.yearserved}</td>
                                                        <td className="light-td">{r.rootchapter || "—"}</td>

                                                        <td className="light-td" style={{ whiteSpace: "nowrap" }}>
                                                            <button
                                                                className="btn-primary"
                                                                style={{ marginRight: "6px" }}
                                                                onClick={() => openEdit(r)}
                                                            >
                                                                Edit
                                                            </button>

                                                            <button
                                                                className="btn-secondary"
                                                                onClick={() => deleteRow(r)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* 🔹 Pager — NOW INSIDE WHITE BOX */}
                                <div
                                    className="table-footer"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        marginTop: 10,
                                    }}
                                >
                                    <div className="muted">
                                        Showing{" "}
                                        <strong>{rows.length ? page * pageLen + 1 : 0}</strong> to{" "}
                                        <strong>{Math.min((page + 1) * pageLen, total)}</strong> of{" "}
                                        <strong>{total}</strong> entries (Legacy)
                                    </div>

                                    <div
                                        style={{
                                            marginLeft: "auto",
                                            display: "flex",
                                            gap: 8,
                                        }}
                                    >
                                        <button
                                            disabled={page === 0}
                                            onClick={() =>
                                                loadList({ pageIndex: Math.max(page - 1, 0) })
                                            }
                                            className="btn-secondary"
                                        >
                                            Prev
                                        </button>

                                        <span className="pager-badge">
                                            Page {page + 1} / {totalPages}
                                        </span>

                                        <button
                                            disabled={(page + 1) * pageLen >= total}
                                            onClick={() =>
                                                loadList({
                                                    pageIndex: Math.min(
                                                        page + 1,
                                                        totalPages - 1
                                                    ),
                                                })
                                            }
                                            className="btn-secondary"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>

                            </div>


                            {/* 🔹 Edit Modal */}
                            {editRow && (
                                <div
                                    className="modal-overlay"
                                    onClick={closeEdit}
                                >
                                    <div
                                        className="modal-box"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            className="close-button"
                                            onClick={closeEdit}
                                        >
                                            ✕
                                        </button>

                                        <div className="modal-top">
                                            <h3>Edit Legacy Entry</h3>
                                            <div className="muted">
                                                Primary ID: {editRow.primaryid}
                                            </div>
                                            <div className="muted">
                                                Name: {editRow.lastname},{" "}
                                                {editRow.firstname}
                                            </div>
                                        </div>

                                        <div className="modal-grid">
                                            <div>
                                                <label>
                                                    <strong>Year Served</strong>
                                                </label>
                                                <input
                                                    value={editYear}
                                                    onChange={(e) =>
                                                        setEditYear(e.target.value)
                                                    }
                                                    placeholder="2026-1st Half"
                                                    maxLength={15}
                                                />
                                            </div>

                                            {/* ✅ Upload Photo */}
                                            <div>
                                                <label>
                                                    <strong>
                                                        Upload Chairman Photo
                                                        (JPG/PNG)
                                                    </strong>
                                                </label>
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png"
                                                    onChange={(e) =>
                                                        uploadChairmanPhoto(
                                                            editRow.primaryid,
                                                            e.target.files[0]
                                                        )
                                                    }
                                                />
                                            </div>

                                            {/* ✅ Current Photo Preview + Remove Button */}
                                            {editRow?.primaryid && (
                                                <div>
                                                    <label><strong>Current Photo</strong></label>

                                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "6px" }}>
                                                        <img
                                                            src={currentPhotoUrl || "/default-placeholder.png"}
                                                            alt="Chairman"
                                                            style={{
                                                                width: "120px",
                                                                height: "120px",
                                                                objectFit: "cover",
                                                                borderRadius: "10px",
                                                                border: "2px solid #DAA520",
                                                                boxShadow: "0 0 10px rgba(0,0,0,0.4)",
                                                            }}
                                                            onError={(e) => (e.currentTarget.src = "/default-placeholder.png")}
                                                        />
                                                    </div>

                                                    <button
                                                        className="btn-secondary"
                                                        style={{
                                                            background: "#b22222",
                                                            color: "#fff",
                                                            padding: "4px 10px",
                                                            borderRadius: "6px",
                                                            fontSize: "0.85rem",
                                                            marginTop: "10px",
                                                            display: "block",
                                                            marginLeft: "auto",
                                                            marginRight: "auto",
                                                        }}
                                                        onClick={() => removeChairmanPhoto(editRow.primaryid)}
                                                    >
                                                        Remove Photo
                                                    </button>
                                                </div>
                                            )}


                                        </div>

                                        <div
                                            className="card-actions"
                                            style={{ marginTop: 14 }}
                                        >
                                            <button
                                                className="btn-primary"
                                                onClick={saveEditRow}
                                                disabled={chairBusy}
                                            >
                                                {chairBusy
                                                    ? "Saving…"
                                                    : "Save Changes"}
                                            </button>
                                            <button
                                                className="btn-secondary"
                                                onClick={closeEdit}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ---------- Archive tab ---------- */}

                    {activeTab === "Archive" && (

                        <>
                            <h3 className="card-title" style={{ marginBottom: "10px" }}>
                                Archived Members
                            </h3>
                            <div
                                className="member-table"
                                style={{
                                    marginTop: 24,
                                    width: "100%",       // ✔ FULL WIDTH
                                    background: "#fff",
                                    borderRadius: "8px",
                                    padding: "10px"
                                }}
                            >
                                {/* Error Message */}
                                {archiveError && (
                                    <div style={{ color: "#ff7676", marginTop: 8 }}>{archiveError}</div>
                                )}

                                {/* ================= SEARCH + SHOW ENTRIES + TOTAL ================= */}
                                <div
                                    className="table-controls"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",   // ✔ nicely spaced
                                        flexWrap: "nowrap",                // ✔ keep in one row
                                        gap: "20px",
                                        width: "100%",
                                        marginBottom: "10px",
                                        marginTop: "5px",
                                    }}
                                >
                                    {/* Left: Show entries */}
                                    <div>
                                        <select
                                            value={archiveRowsPerPage}
                                            onChange={(e) => {
                                                setArchiveRowsPerPage(Number(e.target.value));
                                                setArchivePage(1);
                                            }}
                                            style={{ padding: "6px 8px", borderRadius: "6px" }}
                                        >
                                            <option value={5}>5 | Show entries</option>
                                            <option value={10}>10 | Show entries</option>
                                            <option value={20}>20 | Show entries</option>
                                            <option value={50}>50 | Show entries</option>
                                        </select>
                                    </div>

                                    {/* Center: Total archived members */}
                                    <div
                                        style={{
                                            flex: 1,
                                            textAlign: "center",
                                            fontWeight: "600",
                                            fontSize: "1rem",
                                            color: "goldenrod",
                                        }}
                                    >
                                        Total Archived:{" "}
                                        <strong style={{ color: "#d4af37" }}>{archiveTotal}</strong>
                                    </div>

                                    {/* Right: Search */}
                                    <div>
                                        <input
                                            type="text"
                                            placeholder="Search ID, Name..."
                                            value={archiveSearch}
                                            onChange={(e) => {
                                                setArchiveSearch(e.target.value);
                                                setArchivePage(1);
                                            }}
                                            className="search-input"
                                            style={{
                                                padding: "6px 8px",
                                                borderRadius: 6,
                                                border: "1px solid #bbb",
                                                color: "#333",
                                                background: "#fff",
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* ================= TABLE ================= */}
                                {/* 🔹 Export Button */}
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        marginBottom: "8px",
                                    }}
                                >
                                    <button
                                        className="export-btn"
                                        onClick={() => exportCSV(filteredArchive, "archived_members")}
                                    >
                                        📤 Export CSV
                                    </button>
                                </div>

                                <div
                                    className="table-wrap"
                                    style={{
                                        overflowX: "auto",
                                        marginTop: 10,
                                        background: "#fff",
                                        borderRadius: "8px",
                                        padding: "10px",
                                    }}
                                >
                                    <table
                                        className="nice-table light-table"
                                        style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            tableLayout: "auto",
                                            minWidth: "100%",
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                {[
                                                    { key: "primaryid", label: "Primary ID" },
                                                    { key: "lastname", label: "Last Name" },
                                                    { key: "firstname", label: "First Name" },
                                                    { key: "alias", label: "Alias" },
                                                    { key: "rootchapter", label: "Root Chapter" },
                                                    { key: "gender", label: "Gender" },
                                                    { key: "archived_at", label: "Archived At" },
                                                    { key: "_actions", label: "Actions" },
                                                ].map((col) => (
                                                    <th
                                                        key={col.key}
                                                        onClick={() =>
                                                            col.key !== "_actions" && toggleArchiveSort(col.key)
                                                        }
                                                        style={{
                                                            cursor: col.key !== "_actions" ? "pointer" : "default",
                                                        }}
                                                    >
                                                        {col.label}
                                                        {archiveOrderBy === col.key &&
                                                            (archiveOrderDir === "asc" ? " ▲" : " ▼")}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {visibleArchiveRows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={8} className="light-td center">
                                                        No archived members found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                visibleArchiveRows.map((r) => (
                                                    <tr key={r.id} className="light-tr">
                                                        <td className="light-td">{r.primaryid}</td>
                                                        <td className="light-td">{r.lastname}</td>
                                                        <td className="light-td">{r.firstname}</td>
                                                        <td className="light-td">{r.alias || "—"}</td>
                                                        <td className="light-td">{r.rootchapter || "—"}</td>
                                                        <td className="light-td">{r.gender}</td>
                                                        <td className="light-td">
                                                            {new Date(r.archived_at).toLocaleString()}
                                                        </td>
                                                        <td
                                                            className="light-td"
                                                            style={{
                                                                textAlign: "center",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            <button
                                                                className="btn-primary"
                                                                style={{
                                                                    marginRight: "6px",
                                                                    minWidth: "70px",
                                                                }}
                                                                onClick={() => activateMember(r)}
                                                            >
                                                                Activate
                                                            </button>

                                                            <button
                                                                className="btn-danger"
                                                                style={{
                                                                    minWidth: "70px",
                                                                }}
                                                                onClick={() => deleteArchiveMember(r)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ================= PAGINATION ================= */}
                                <div
                                    className="table-footer"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        marginTop: 10,

                                    }}
                                >
                                    <div className="muted">
                                        Showing{" "}
                                        <strong>{visibleArchiveRows.length ? startIndex + 1 : 0}</strong> to{" "}
                                        <strong>
                                            {Math.min(startIndex + archiveRowsPerPage, filteredArchive.length)}
                                        </strong>{" "}
                                        of <strong>{filteredArchive.length}</strong> entries (Archived)
                                    </div>

                                    <div
                                        style={{
                                            marginLeft: "auto",
                                            display: "flex",
                                            gap: 8,
                                            alignItems: "center",
                                        }}
                                    >
                                        <button
                                            disabled={archivePage === 1}
                                            onClick={() => setArchivePage(archivePage - 1)}
                                            className="btn-secondary"
                                        >
                                            Prev
                                        </button>
                                        <span className="pager-badge">
                                            Page {archivePage} / {totalArchivePages || 1}
                                        </span>
                                        <button
                                            disabled={
                                                archivePage === totalArchivePages || totalArchivePages === 0
                                            }
                                            onClick={() => setArchivePage(archivePage + 1)}
                                            className="btn-secondary"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* ================= SECURITY VIEW ================= */}
            {view === "security" && (
                <div className="member-table">
                    <h3>Security - User Password Reset</h3>

                    <div className="security-table-card">
                        <div className="security-controls">
                            <div className="security-controls-top">
                                <select
                                    value={securityRowsPerPage}
                                    onChange={(e) => setSecurityRowsPerPage(Number(e.target.value))}
                                >
                                    <option value={25}>25 | Show entries</option>
                                    <option value={50}>50 | Show entries</option>
                                    <option value={100}>100 | Show entries</option>
                                </select>

                                <select
                                    value={securityPrimaryFilter}
                                    onChange={(e) => setSecurityPrimaryFilter(e.target.value)}
                                >
                                    <option value="all">All User IDs</option>
                                    <option value="PHI">PHI only</option>
                                    <option value="SIG">SIG only</option>
                                </select>
                            </div>

                            <div className="security-total">
                                Total Users {filteredSecurityRows.length} entries
                            </div>

                            <div className="security-controls-bottom">
                                <input
                                    type="text"
                                    placeholder="Search username, name, ID, email..."
                                    value={securitySearch}
                                    onChange={(e) => setSecuritySearch(e.target.value)}
                                />

                                <button className="security-refresh-btn" onClick={loadSecurityUsers} disabled={securityLoading}>
                                    {securityLoading ? "Loading..." : "Refresh"}
                                </button>
                            </div>
                        </div>

                        {securityMsg && (
                            <div
                                style={{
                                    margin: "0 0 12px",
                                    color: /password reset for|credentials sent/i.test(securityMsg) ? "#148a35" : "#c62828",
                                    fontWeight: 600,
                                }}
                            >
                                {securityMsg}
                            </div>
                        )}

                        <div className="security-table-wrap">
                            <table className="nice-table light-table security-table">
                                <thead>
                                    <tr>
                                        <th className="light-th"><button type="button" className="security-sort-btn" onClick={() => toggleSecuritySort("primaryid")}>Primary ID {securitySort.key === "primaryid" && (securitySort.direction === "asc" ? "▲" : "▼")}</button></th>
                                        <th className="light-th"><button type="button" className="security-sort-btn" onClick={() => toggleSecuritySort("firstname")}>First Name {securitySort.key === "firstname" && (securitySort.direction === "asc" ? "▲" : "▼")}</button></th>
                                        <th className="light-th"><button type="button" className="security-sort-btn" onClick={() => toggleSecuritySort("lastname")}>Last Name {securitySort.key === "lastname" && (securitySort.direction === "asc" ? "▲" : "▼")}</button></th>
                                        <th className="light-th">Username</th>
                                        <th className="light-th"><button type="button" className="security-sort-btn" onClick={() => toggleSecuritySort("role")}>Role {securitySort.key === "role" && (securitySort.direction === "asc" ? "▲" : "▼")}</button></th>
                                        <th className="light-th">Email</th>
                                        <th className="light-th">Active</th>
                                        <th className="light-th">Reset Required</th>
                                        <th className="light-th">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleSecurityRows.length === 0 ? (
                                        <tr>
                                            <td className="light-td center" colSpan={9}>
                                                {securityLoading ? "Loading users..." : "No users found."}
                                            </td>
                                        </tr>
                                    ) : (
                                        visibleSecurityRows.map((row) => (
                                            <tr key={row.idname}>
                                                <td className="light-td security-primaryid-cell">{row.primaryid || "-"}</td>
                                                <td className="light-td">{row.firstname || "-"}</td>
                                                <td className="light-td">{row.lastname || "-"}</td>
                                                <td className="light-td">{row.idname}</td>
                                                <td className="light-td">
                                                    {canManageSecurityRoles &&
                                                        !["creator", "adminsoal"].includes(String(row.idname || "").toLowerCase()) ? (
                                                        <select
                                                            className="security-role-select"
                                                            value={(row.role || "user").toLowerCase()}
                                                            disabled={securityRoleBusy === row.idname}
                                                            onChange={(e) => updateSecurityRole(row.idname, e.target.value)}
                                                            title="Change login role"
                                                        >
                                                            <option value="user">User</option>
                                                            <option value="admin">Admin</option>
                                                            <option value="comelec">Comelec</option>
                                                        </select>
                                                    ) : (
                                                        row.role || "-"
                                                    )}
                                                </td>
                                                <td className="light-td">{row.email || "-"}</td>
                                                <td className="light-td">{row.active ? "Yes" : "No"}</td>
                                                <td className="light-td">
                                                    {row.password_change_required ? "Yes" : "No"}
                                                </td>
                                                <td className="light-td">
                                                    <button
                                                        className={`security-reset-btn ${row.uses_default_password ? "default-password" : "custom-password"}`}
                                                        onClick={() => resetSecurityPassword(row.idname)}
                                                        title={
                                                            row.uses_default_password
                                                                ? "Still using the default password"
                                                                : "Password was already changed"
                                                        }
                                                    >
                                                        Reset
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="security-pager">
                            <button
                                className="security-page-btn"
                                disabled={securityPage === 1}
                                onClick={() => setSecurityPage((p) => Math.max(1, p - 1))}
                            >
                                Prev
                            </button>
                            <span className="pager-badge">
                                Page {securityPage} of {totalSecurityPages || 1}
                            </span>
                            <button
                                className="security-page-btn"
                                disabled={securityPage === totalSecurityPages || totalSecurityPages === 0}
                                onClick={() => setSecurityPage((p) => Math.min(totalSecurityPages || 1, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>

                </div>
            )}

            {view === "ovsPassword" && canEditOvsPassword && (
                <div className="ovs-password-panel">
                    <div className="ovs-password-card">
                        <button
                            type="button"
                            className="ovs-password-close"
                            onClick={closeOvsPasswordView}
                            aria-label="Close OVS password editor"
                        >
                            ×
                        </button>
                        <h2>OVS - Edit Password</h2>
                        <p className="ovs-password-note">
                            Use this when a new COMELEC team takes over. After saving, the old OVS password will no longer unlock the Online Voting System.
                        </p>

                        <form className="ovs-password-form" onSubmit={saveOvsPassword}>
                            <label>
                                Current OVS Password
                                <div className="ovs-password-input-wrap">
                                    <input
                                        type={ovsPasswordVisible.currentPassword ? "text" : "password"}
                                        name="currentPassword"
                                        value={ovsPasswordForm.currentPassword}
                                        onChange={handleOvsPasswordChange}
                                        placeholder="Enter current OVS password"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className="ovs-password-eye"
                                        onClick={() => toggleOvsPasswordVisible("currentPassword")}
                                        aria-label={ovsPasswordVisible.currentPassword ? "Hide current password" : "Show current password"}
                                    >
                                        👁
                                    </button>
                                </div>
                            </label>

                            <label>
                                New OVS Password
                                <div className="ovs-password-input-wrap">
                                    <input
                                        type={ovsPasswordVisible.newPassword ? "text" : "password"}
                                        name="newPassword"
                                        value={ovsPasswordForm.newPassword}
                                        onChange={handleOvsPasswordChange}
                                        placeholder="Enter new OVS password"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="ovs-password-eye"
                                        onClick={() => toggleOvsPasswordVisible("newPassword")}
                                        aria-label={ovsPasswordVisible.newPassword ? "Hide new password" : "Show new password"}
                                    >
                                        👁
                                    </button>
                                </div>
                            </label>

                            <label>
                                Confirm New Password
                                <div className="ovs-password-input-wrap">
                                    <input
                                        type={ovsPasswordVisible.confirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={ovsPasswordForm.confirmPassword}
                                        onChange={handleOvsPasswordChange}
                                        placeholder="Confirm new OVS password"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="ovs-password-eye"
                                        onClick={() => toggleOvsPasswordVisible("confirmPassword")}
                                        aria-label={ovsPasswordVisible.confirmPassword ? "Hide confirm password" : "Show confirm password"}
                                    >
                                        👁
                                    </button>
                                </div>
                            </label>

                            {ovsPasswordMsg && (
                                <div
                                    className={`ovs-password-message ${
                                        ovsPasswordMsg.toLowerCase().includes("updated") ? "success" : "error"
                                    }`}
                                >
                                    {ovsPasswordMsg}
                                </div>
                            )}

                            <div className="ovs-password-actions">
                                <button
                                    className="ovs-password-save"
                                    type="submit"
                                    disabled={ovsPasswordSaving || ovsPasswordResetting}
                                >
                                    {ovsPasswordSaving ? "Saving..." : "Save OVS Password"}
                                </button>
                                <button
                                    className="ovs-password-reset"
                                    type="button"
                                    onClick={resetOvsPassword}
                                    disabled={ovsPasswordSaving || ovsPasswordResetting}
                                >
                                    {ovsPasswordResetting ? "Resetting..." : "Reset Password"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ================= REPORT VIEW ================= */}
            {view === "report" && (
                <div className="report-section">
                    <SoalReports />
                </div>
            )}

            {view === "voting" && <AdminVoting role={role} />}

            {view === "finishedVoting" && <AdminVoting role={role} mode="finished" />}

            {view === "calendar" && <AdminCalendar />}

            {messageBox.show && (
                <div className="center-popup">
                    <div
                        className="popup-box"
                        style={{
                            background:
                                messageBox.success === null
                                    ? "#333"
                                    : messageBox.success
                                        ? "#1e8e3e"
                                        : "#b22222",
                            color: "#fff",
                            padding: "20px 30px",
                            borderRadius: "10px",
                            textAlign: "center",
                            boxShadow: "0 0 15px rgba(0,0,0,0.4)",
                            fontSize: "1rem",
                            minWidth: "280px",
                        }}
                    >
                        <div>{messageBox.text}</div>
                        {messageBox.success === null && (
                            <div style={{ marginTop: 15, display: "flex", justifyContent: "center", gap: 10 }}>
                                <button
                                    className="btn-primary"
                                    onClick={() => {
                                        if (pendingAction) pendingAction();
                                        setMessageBox({ show: false, text: "", success: false });
                                    }}
                                >
                                    OK
                                </button>
                                <button
                                    className="btn-secondary"
                                    onClick={() => {
                                        setPendingAction(null);
                                        setMessageBox({ show: false, text: "", success: false });
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {cropModalOpen && (
                <CropModal
                    file={cropImageFile}
                    onCancel={() => {
                        setCropModalOpen(false);
                        setCropImageFile(null);
                    }}
                    onCropComplete={handleCroppedUpload}
                />
            )}

            {uploading && (
                <div className="upload-spinner-popup">
                    <div
                        className="popup-box"
                        style={{
                            background: "#000",
                            color: "#fff",
                            padding: "20px 30px",
                            borderRadius: "12px",
                            textAlign: "center",
                            boxShadow: "0 0 15px rgba(0,0,0,0.4)"
                        }}
                    >
                        <div
                            style={{
                                width: "45px",
                                height: "45px",
                                border: "5px solid #444",
                                borderTopColor: "gold",
                                borderRadius: "50%",
                                margin: "0 auto 12px",
                                animation: "spin 0.8s linear infinite"
                            }}
                        />
                        Uploading Photo…
                    </div>
                </div>
            )}

        </section>
    )
}
